import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import MenuItem, { type MenuItemProps } from '@mui/material/MenuItem';
import Stack, { type StackProps } from '@mui/material/Stack';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from '@tyro/i18n';
import { type ChangeEvent, type ReactNode, useMemo, useState } from 'react';
import { Avatar, type AvatarProps } from '../avatar';

type SelectCustomVariant = Omit<TextFieldProps, 'variant'> & {
  variant?:
    | TextFieldProps['variant']
    | 'white-filled'
    | 'white-filled-outlined'
    | 'soft';
};

type SelectedValue<
  TOption,
  TMultiple extends boolean | undefined,
> = TMultiple extends true ? TOption[] : TOption;

type ValueProp<
  TOption,
  TMultiple extends boolean | undefined,
> = TMultiple extends true
  ? Array<TOption | string | number>
  : TOption | string | number | undefined;

export type SelectProps<
  TSelectOption,
  TMultiple extends boolean | undefined = false,
> = SelectCustomVariant & {
  options: TSelectOption[];
  multiple?: TMultiple;
  getOptionLabel?: (option: TSelectOption) => string | ReactNode;
  customSelectRef?: TextFieldProps['ref'];
  optionIdKey?: keyof TSelectOption;
  optionTextKey?: TSelectOption extends object ? keyof TSelectOption : never;
  value?: ValueProp<TSelectOption, TMultiple>;
  renderValue?: (option: SelectedValue<TSelectOption, TMultiple>) => ReactNode;
  renderAvatarOption?: (
    option: TSelectOption,
    renderOption: (
      avatarProps: AvatarProps & {
        caption?: string | null;
        captionProps?: TypographyProps;
        hideAvatar?: boolean;
      },
      stackProps?: StackProps,
    ) => React.ReactNode,
  ) => React.ReactNode;
  menuItemProps?: MenuItemProps;
  loading?: boolean;
  headerComponent?: ReactNode;
  showSelectAll?: boolean;
};

export const Select = <
  TSelectOption extends string | number | object,
  TMultiple extends boolean | undefined = false,
>({
  options,
  optionIdKey,
  optionTextKey,
  customSelectRef,
  getOptionLabel,
  variant,
  sx,
  SelectProps,
  renderValue,
  value,
  menuItemProps,
  loading,
  headerComponent,
  renderAvatarOption,
  slotProps,
  multiple,
  showSelectAll,
  ...textFieldProps
}: SelectProps<TSelectOption, TMultiple>) => {
  const { spacing, palette } = useTheme();
  const { t } = useTranslation(['common']);
  const [open, setOpen] = useState(false);

  const enableSelectAll = multiple && showSelectAll;
  const shouldControlOpen = !!multiple;

  const allOptionValues = useMemo(
    () =>
      options.map((option) =>
        optionIdKey ? (option[optionIdKey] as string | number) : String(option),
      ),
    [options, optionIdKey],
  );

  const currentValues = Array.isArray(value) ? value : [];
  const allSelected =
    enableSelectAll &&
    options.length > 0 &&
    currentValues.length === options.length;

  const handleSelectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const newValue = allSelected ? [] : allOptionValues;
    const mockEvent = {
      target: { value: newValue },
    } as unknown as ChangeEvent<HTMLInputElement>;

    textFieldProps.onChange?.(mockEvent);
  };

  const isWhiteFilledVariant =
    variant === 'white-filled' || variant === 'white-filled-outlined';
  const isSoftVariant = variant === 'soft';
  const customVariant = isWhiteFilledVariant || isSoftVariant;

  const optionSelected = useMemo<
    SelectedValue<TSelectOption, TMultiple>
  >(() => {
    if (!optionIdKey) {
      return value as SelectedValue<TSelectOption, TMultiple>;
    }

    if (Array.isArray(value)) {
      return value.map((valueOption) =>
        options.find(
          (currentOption) => currentOption[optionIdKey] === valueOption,
        ),
      ) as SelectedValue<TSelectOption, TMultiple>;
    }

    return options.find(
      (currentOption) => currentOption[optionIdKey] === value,
    ) as SelectedValue<TSelectOption, TMultiple>;
  }, [options, optionIdKey, value]);

  return (
    <TextField
      {...textFieldProps}
      value={value}
      variant={customVariant ? 'filled' : variant}
      ref={customSelectRef}
      select
      slotProps={{
        ...slotProps,
        // @ts-expect-error
        select: {
          ...SelectProps,
          ...slotProps?.select,
          multiple,
          ...(shouldControlOpen && {
            open,
            onOpen: () => setOpen(true),
            onClose: () => {},
          }),
          MenuProps: {
            ...SelectProps?.MenuProps,
            ...(multiple && {
              onClose: (
                _event: object,
                reason: 'backdropClick' | 'escapeKeyDown' | 'tabKeyDown',
              ) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                  setOpen(false);
                }
              },
            }),
            sx: {
              maxHeight: spacing(44),
              ...SelectProps?.MenuProps?.sx,
            },
          },
          ...(renderValue && {
            renderValue: () =>
              optionSelected ? (
                <Stack flexDirection="row" gap={1} alignItems="center">
                  {loading && <CircularProgress size={16} />}
                  {renderValue(optionSelected)}
                </Stack>
              ) : null,
          }),
        },
      }}
      sx={{
        ...sx,
        ...(isWhiteFilledVariant && {
          '& .MuiFilledInput-root, & .MuiFilledInput-root:focus, & .MuiFilledInput-root.Mui-focused, & .MuiFilledInput-root:hover, & .MuiFilledInput-root, & .MuiFilledInput-input:focus, & .MuiFilledInput-input.Mui-focused, & .MuiFilledInput-input:hover':
            {
              backgroundColor: palette.background.default,
              borderRadius: spacing(1),
            },
          ...(variant === 'white-filled-outlined' && {
            '& .MuiFilledInput-root': {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '& > input': {
                pt: '24px',
                pb: '7px',
              },
            },
            '& .MuiInputBase-root:hover': {
              backgroundColor: palette.primary.lighter,
            },
          }),
        }),
        ...(isSoftVariant && {
          '& .MuiSelect-select.MuiSelect-filled.MuiInputBase-input.MuiFilledInput-input':
            {
              paddingRight: spacing(5),
            },
          '& .MuiFilledInput-root, & .MuiFilledInput-root:focus, & .MuiFilledInput-root.Mui-focused, & .MuiFilledInput-root:hover, & .MuiFilledInput-root, & .MuiFilledInput-input:focus, & .MuiFilledInput-input.Mui-focused, & .MuiFilledInput-input:hover':
            {
              backgroundColor: palette.indigo[50016],
              color: palette.indigo.dark,
              borderRadius: spacing(1),
            },
        }),
      }}
    >
      {enableSelectAll && (
        <Stack component="li" mx={1} gap={1}>
          <Stack
            direction="row"
            alignItems="center"
            onClick={handleSelectAll}
            sx={{
              cursor: 'pointer',
              py: 0.75,
              px: 1,
              borderRadius: 0.5,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Checkbox
              checked={currentValues.length > 0}
              indeterminate={currentValues.length > 0 && !allSelected}
              sx={{
                ml: -0.5,
              }}
            />
            <Typography variant="body1">
              {t('common:actions.selectAll')}
            </Typography>
          </Stack>
          <Divider sx={{ borderColor: 'slate.200' }} />
        </Stack>
      )}
      {headerComponent && (
        <Stack gap={1} component="li" mx={1}>
          {headerComponent}
          <Divider sx={{ borderColor: 'slate.200' }} />
        </Stack>
      )}
      {options.map((option) => {
        const optionValue = optionIdKey
          ? (option[optionIdKey] as string | number)
          : String(option);
        const optionLabel = optionTextKey
          ? (option[optionTextKey] as string)
          : getOptionLabel?.(option);

        return (
          <MenuItem key={optionValue} value={optionValue} {...menuItemProps}>
            {renderAvatarOption
              ? renderAvatarOption(
                  option,
                  (
                    { caption, captionProps, hideAvatar, ...avatarProps },
                    stackProps,
                  ) => (
                    <Stack
                      direction="row"
                      gap={2}
                      alignItems="center"
                      {...stackProps}
                    >
                      {hideAvatar ? null : (
                        <Avatar size={32} {...avatarProps} />
                      )}
                      <Stack>
                        <Typography variant="subtitle2">
                          {avatarProps.name}
                        </Typography>
                        {caption && (
                          <Typography variant="caption" {...captionProps}>
                            {caption}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  ),
                )
              : (optionLabel ?? optionValue)}
          </MenuItem>
        );
      })}
    </TextField>
  );
};
